import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useCart } from "@/lib/cart";
import { trackEvent, gaItem } from "@/lib/analytics";
import { CheckCircle2 } from "lucide-react";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ payment_status: "pending", amount: null });
  const [tries, setTries] = useState(0);
  const { clear } = useCart();

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;

    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        setStatus(data);
        if (data.payment_status === "paid") {
          // GA4 purchase — exactly once per order, survives page reloads
          const marker = `ga_purchase_${sessionId}`;
          if (!localStorage.getItem(marker)) {
            localStorage.setItem(marker, "1");
            trackEvent("purchase", {
              transaction_id: data.order_id || sessionId,
              currency: "USD",
              value: Number(data.amount) || 0,
              shipping: Number(data.shipping_total) || 0,
              items: (data.items || []).map(gaItem),
            });
          }
          clear();
          return;
        }
        if (tries < 10) {
          setTries((t) => t + 1);
          setTimeout(poll, 2000);
        }
      } catch {
        if (tries < 10) {
          setTries((t) => t + 1);
          setTimeout(poll, 2000);
        }
      }
    };
    poll();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const paid = status.payment_status === "paid";

  return (
    <div className="pt-28 md:pt-40 min-h-screen max-w-[1440px] mx-auto px-6 md:px-10 pb-24" data-testid="checkout-success">
      <div className="max-w-2xl">
        <p className="font-mono text-[10px] uppercase tracking-editorial text-[#7A2021]">Ch. 08 — Order confirmed</p>
        <h1 className="font-serif text-6xl md:text-8xl tracking-tight leading-none mt-3">
          Thank <em className="font-light">you.</em>
        </h1>
        <div className="mt-10 flex items-start gap-4 border-t border-[#E5E1D8] pt-8">
          <CheckCircle2 className={paid ? "text-[#7A2021]" : "text-[#1A1714]/40"} size={28} />
          <div>
            <p className="font-serif text-2xl">
              {paid ? "Your books are on their way." : "Confirming your order…"}
            </p>
            <p className="font-mono text-xs text-[#1A1714]/60 mt-2">
              {paid && status.amount ? `Charged: $${status.amount.toFixed(2)} · Order ${status.order_id}` : "This may take a few seconds."}
            </p>
          </div>
        </div>
        <Link to="/shop" data-testid="success-continue" className="mt-10 inline-block border border-[#1A1714] px-6 py-3 font-mono text-[11px] uppercase tracking-editorial hover:bg-[#1A1714] hover:text-[#F9F6F0] transition-colors">
          Return to the shelf →
        </Link>
      </div>
    </div>
  );
}
